import { atom } from 'jotai';
import { TokenService } from '@/services/auth/tokenService';
import { selectedClientIdAtom } from '@/state/clients/atoms';
import { createApiClient } from '@/services/api/httpClient';
import {
  PodLKInfo,
  DPPodLKInfo,
  MicrophoneInfo,
  CameraInfo,
} from '@/types/api/clients';
import { createAuthenticatedRequest } from '@/components/camera/axiosApi';

// Sensorpods response interface
export interface SensorPodsResponse {
  data: {
    sensorpod_info: Array<{
      pod_name: string;
      dp_name: string;
      microphone_id?: string;
      rtsp?: string;
      whip?: string;
      log_file?: string;
    }>;
  };
}

// Deterrence pods response interface
interface DeterrencePodsResponse {
  deterrence_pods: DPPodLKInfo[];
}

// Sensorpod info interface
export interface SensorPodInfo {
  pod_name: string;
  dp_name: string;
  microphone_id?: string;
  rtsp?: string;
  whip?: string;
  log_file?: string;
}

// Atom for fetching sensorpods data
export const sensorPodsAtom = atom(async (get) => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();
  const selectedClientId = get(selectedClientIdAtom);

  if (!authToken || !selectedClientId) {
    return [];
  }
  const apiUrl = process.env.PUBLIC_API_URL || 'http://localhost:8080';
  const axios = await createAuthenticatedRequest(apiUrl, authToken);
  const response = await axios.get('/sensorpods', {
    params: { client_id: selectedClientId },
  });

  return response.data.data.sensorpod_info || [];
});

// Atom for fetching deterrence pods data
export const deterrencePodsAtom = atom(async (get) => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();
  const selectedClientId = get(selectedClientIdAtom);

  if (!authToken || !selectedClientId) {
    return [];
  }

  const apiClient = createApiClient(() => authToken);
  const response = await apiClient.get<DeterrencePodsResponse>(
    `/client/${selectedClientId}/deterrencepods`,
  );

  return response.deterrence_pods || [];
});

// Derived atom for pod names (sensorpods)
export const sensorPodNamesAtom = atom(async (get) => {
  const sensorPods = await get(sensorPodsAtom);
  return sensorPods.map(
    (pod: SensorPodInfo): PodLKInfo => ({
      pod_name: pod.pod_name,
      dp_name: pod.dp_name,
    }),
  );
});

// Derived atom for deterrence pod names
export const deterrencePodNamesAtom = atom(async (get) => {
  const deterrencePods = await get(deterrencePodsAtom);
  return deterrencePods.map(
    (pod: DPPodLKInfo): PodLKInfo => ({
      pod_name: pod.dp_name,
      dp_name: pod.dp_name,
    }),
  );
});

// Combined atom for all pod names
export const allPodNamesAtom = atom(async (get) => {
  const sensorPodNames = await get(sensorPodNamesAtom);
  const deterrencePodNames = await get(deterrencePodNamesAtom);
  return [...sensorPodNames, ...deterrencePodNames];
});

// Derived atom for microphones
export const microphonesAtom = atom(async (get) => {
  const sensorPods = await get(sensorPodsAtom);
  return sensorPods.map(
    (microphone_info: SensorPodInfo): MicrophoneInfo => ({
      dp_name: microphone_info.dp_name,
      pod_name: microphone_info.pod_name,
      microphone_id: microphone_info.microphone_id || '',
      rtsp: microphone_info.rtsp || '',
      whip: microphone_info.whip || '',
      log_file: microphone_info.log_file || '',
    }),
  );
});

// Atom for fetching cameras data
export const camerasAtom = atom(async (get) => {
  const tokenService = TokenService.getInstance();
  const authToken = await tokenService.getCurrentToken();
  const selectedClientId = get(selectedClientIdAtom);

  if (!authToken || !selectedClientId) {
    return [];
  }

  const apiClient = createApiClient(() => authToken);
  const response = await apiClient.get<{ cameras: CameraInfo[] }>(
    `/client/${selectedClientId}/cameras`,
  );

  return response.cameras || [];
});
